import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Records how long a signed-in user spends on each page, for the Admin Dashboard's Activity
// page. Every send goes through navigator.sendBeacon, which the browser queues and delivers in
// the background — it never blocks or delays navigation (no response the page waits on), so
// this can't slow the site down. Falls back to nothing (just skips) in the rare environment
// without sendBeacon rather than risk a blocking fetch on page unload.
//
// Mounted once, inside ProtectedRoute, alongside Navigation/DemoBanner — see App.js.
const MAX_DURATION_SECONDS = 3600; // matches _MAX_PAGE_VIEW_SECONDS in app.py

function send(page, enteredAt) {
  if (!page || typeof navigator === 'undefined' || !navigator.sendBeacon) return;
  const durationSeconds = Math.min(MAX_DURATION_SECONDS, Math.max(0, Math.round((Date.now() - enteredAt) / 1000)));
  // A page visited for under a second (a redirect bounce, a double-click) isn't a real view.
  if (durationSeconds < 1) return;
  const blob = new Blob([JSON.stringify({ page, enteredAt, durationSeconds })], { type: 'application/json' });
  try {
    navigator.sendBeacon('/api/activity/log-pageview', blob);
  } catch {
    // sendBeacon can throw if the payload is rejected (e.g. over the browser's queued-bytes
    // quota) — never worth surfacing to the user over a tracking call.
  }
}

function PageViewTracker() {
  const location = useLocation();
  // {page, enteredAt} for whatever page is currently showing — flushed (sent) right before it
  // changes to something else, not while it's still the active page.
  const currentRef = useRef({ page: location.pathname, enteredAt: Date.now() });

  // Route change: close out the PREVIOUS page's visit, then start timing the new one. Skips the
  // very first render (nothing to close out yet — currentRef already seeded with it above).
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    send(currentRef.current.page, currentRef.current.enteredAt);
    currentRef.current = { page: location.pathname, enteredAt: Date.now() };
  }, [location.pathname]);

  // Tab closed/refreshed/backgrounded: close out whatever page is still showing. 'pagehide'
  // (not just 'beforeunload') also covers mobile Safari's back-forward-cache suspend, and
  // 'visibilitychange' catches a plain tab-switch away, which beforeunload never fires for.
  useEffect(() => {
    const flushCurrent = () => send(currentRef.current.page, currentRef.current.enteredAt);
    // On a plain tab-switch (not a close), reset the clock after flushing — otherwise coming
    // back to the same tab and eventually navigating away would re-send the already-flushed
    // time on top of the new time, double-counting it.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flushCurrent();
        currentRef.current = { ...currentRef.current, enteredAt: Date.now() };
      }
    };
    window.addEventListener('pagehide', flushCurrent);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flushCurrent);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return null;
}

export default PageViewTracker;
